<template>
  <!-- THE birth component (design 2026-10-26, Claude outputs/birthday-ask-2026-10-26).
       Every place that asks for a birth asks with this: the gift room and
       Altar Home (through BirthAsk), the Mirror, Our stars, the birthday-request
       link, the chat and the Studio.
         the ask          fields include 'date': Month / Day / Year, one why
                          line, "Show my star sign". An optional time and place
                          fold behind "I know my birth time too".
         the closer look  fields are time and/or place only: the day arc and the
                          place, each savable alone; the button says what it saves.
       Pure UI: it emits what was chosen; each surface owns its save. -->
  <component :is="shell" v-bind="shellProps" @close="$emit('close')">
    <div class="bm" :class="'bm--' + mode" role="group" :aria-label="title || COPY.dateLabel">
      <header v-if="title || explain || isClosable" class="bm-head">
        <h3 v-if="title" class="bm-title">{{ title }}</h3>
        <div class="bm-headr">
          <button v-if="explain" type="button" class="bm-q" :class="{ pulse: !explained && !reduced }" aria-haspopup="dialog"
                  data-testid="bm-explain" @click="openExplainer"><b aria-hidden="true">?</b>{{ COPY.q }}</button>
          <button v-if="isClosable" type="button" class="bm-close" aria-label="Close" @click="$emit('close')">×</button>
        </div>
      </header>
      <p v-if="subtitle" class="bm-why">{{ subtitle }}</p>
      <p v-else-if="isCloser" class="bm-why">Your birth time and place show your <em>Rising sign</em> and settle your
        <em>Moon</em>. Add what you know. Each one saves on its own.</p>

      <!-- Date: Month / Day / Year (autofillable, plain in any locale) -->
      <div v-if="has('date')" class="bm-field">
        <span :id="uid + 'dl'" class="bm-label">{{ COPY.dateLabel }}</span>
        <div class="bm-date3" role="group" :aria-labelledby="uid + 'dl'">
          <select ref="monthEl" v-model="parts.month" class="bm-select" aria-label="Month" autocomplete="bday-month" data-testid="bm-month">
            <option value="">Month</option>
            <option v-for="(m, i) in MONTHS" :key="m" :value="String(i + 1)">{{ m }}</option>
          </select>
          <select v-model="parts.day" class="bm-select" aria-label="Day" autocomplete="bday-day" data-testid="bm-day">
            <option value="">Day</option>
            <option v-for="d in dayCount" :key="d" :value="String(d)">{{ d }}</option>
          </select>
          <input v-model="parts.year" class="bm-input" inputmode="numeric" maxlength="4" placeholder="Year" aria-label="Year"
                 autocomplete="bday-year" data-testid="bm-year" @input="onYear" @blur="touched.date = true" />
        </div>
        <p v-if="shown.birthdate" class="bm-error" data-testid="bm-error-birthdate">{{ shown.birthdate }}</p>
      </div>

      <!-- The fold: time and place, offered, never pushed -->
      <button v-if="foldable" type="button" class="bm-more" :aria-expanded="String(foldOpen)" data-testid="bm-more"
              @click="foldOpen = !foldOpen">
        <span class="plus" aria-hidden="true">+</span>
        <span>{{ COPY.more }}<small>{{ COPY.moreSub }}</small></span>
      </button>

      <div v-if="showTimePlace" :class="{ 'bm-closer-in': foldable }">
        <!-- Time: the day arc, plus "I know the exact time" and "I don't know" -->
        <div v-if="has('time')" class="bm-field">
          <span :id="uid + 'tl'" class="bm-label">{{ isCloser || foldable ? COPY.timeLabel : 'Time of birth' }}
            <i v-if="isOptional('time')">{{ COPY.optional }}</i></span>
          <BirthDayArc :time-kind="form.timeKind" :window-key="form.windowKey" :exact-time="form.exactTime" :reduced="reduced"
                       :labelledby="uid + 'tl'" @pick="pickWindow" />
          <p class="arc-read" :class="{ idle: readout.idle }" aria-live="polite" data-testid="bm-arc-read">
            {{ readout.main }}<span v-if="readout.sub"> · {{ readout.sub }}</span></p>
          <div class="arc-links">
            <button type="button" :class="{ on: form.timeKind === 'exact' }" data-testid="bm-time-exact" @click="pickExact">{{ COPY.exactBtn }}</button>
            <span class="dot" aria-hidden="true">·</span>
            <button type="button" :class="{ on: form.timeKind === 'unknown' }" data-testid="bm-time-unknown" @click="pickUnknown">{{ COPY.unknownBtn }}</button>
          </div>
          <div v-if="form.timeKind === 'exact'" class="bm-exact">
            <input ref="exactInput" v-model="form.exactTime" data-testid="bm-exact-time" class="bm-input" type="time" aria-label="Exact time of birth" />
          </div>
          <p v-if="shown.time" class="bm-error" data-testid="bm-error-time">{{ shown.time }}</p>
        </div>

        <!-- Place: a type-ahead with the map behind the pin -->
        <div v-if="has('place')" class="bm-field">
          <label class="bm-label" :for="uid + 'place'">{{ isCloser || foldable ? COPY.placeLabel : 'Birthplace' }}
            <i v-if="isOptional('place')">{{ COPY.optional }}</i></label>
          <div class="bm-place">
            <input :id="uid + 'place'" v-model="placeQuery" data-testid="bm-place-input" class="bm-input"
                   :class="{ picked: form.place && form.place.name }" type="text" autocomplete="off" :placeholder="COPY.placePh"
                   role="combobox" :aria-expanded="String(listOpen)" :aria-controls="uid + 'list'" @input="onPlaceInput"
                   @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)" @keydown.enter.prevent="chooseActive" />
            <button type="button" class="bm-pin" :class="{ on: showMap }" :aria-label="COPY.mapBtn" :title="COPY.mapBtn"
                    :aria-pressed="String(showMap)" data-testid="bm-map-toggle" @click="toggleMap">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></svg>
            </button>
            <ul v-if="listOpen" :id="uid + 'list'" class="bm-results" role="listbox">
              <li v-for="(r, i) in results" :key="r.name + i">
                <button type="button" class="bm-result" :class="{ active: i === active }" data-testid="bm-place-option"
                        role="option" :aria-selected="String(i === active)" @click="choosePlace(r)">{{ r.name }}</button>
              </li>
              <li><button type="button" class="bm-result map" @click="toggleMap">📍 {{ results.length ? COPY.mapRow : COPY.noMatch }}</button></li>
            </ul>
            <p v-else-if="searching" class="bm-searching">Searching…</p>
          </div>
          <p v-if="form.place && !form.place.name" class="bm-chosen" data-testid="bm-place-chosen">📍 {{ COPY.pinned }}</p>
          <div v-if="showMap" class="bm-map">
            <p class="bm-map-help">{{ COPY.mapHelp }}</p>
            <BirthplaceMapWidgetAccount :initial-coordinates="form.place" @submit="onMapPick" />
          </div>
          <p v-if="shown.place" class="bm-error" data-testid="bm-error-place">{{ shown.place }}</p>
        </div>
      </div>

      <p v-if="serverErrors && serverErrors._" class="bm-error">{{ serverErrors._ }}</p>

      <div class="bm-actions">
        <button v-if="dismiss" type="button" class="bm-ghost" data-testid="bm-dismiss" @click="$emit('close')">{{ dismiss }}</button>
        <button type="button" class="bm-save" data-testid="bm-save" :disabled="saving || (isCloser && closer.disabled)" @click="submit">
          {{ saving ? COPY.saving : buttonLabel }}
        </button>
      </div>
      <p v-if="isCloser && closer.hint" class="bm-hint" data-testid="bm-hint">{{ closer.hint }}</p>
      <p v-if="foldable" class="bm-fine" data-testid="bm-fine">{{ COPY.fine }}</p>
      <p v-if="foldable" class="bm-fine bm-privacy" data-testid="bm-privacy">{{ COPY.privacy }}</p>
    </div>
  </component>

  <BirthExplainer v-if="explainOpen" :with-name="withName" :reduced="reduced" @close="explainOpen = false" @start="startFromExplainer" />
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import BirthplaceMapWidgetAccount from '@/components/widgets/BirthplaceMapWidgetAccount.vue'
import BirthDayArc from '@/components/widgets/BirthDayArc.vue'
import BirthExplainer from '@/components/widgets/BirthExplainer.vue'
import BirthSheet from '@/components/widgets/BirthSheet.vue'
import { searchBirthPlaces } from '@/api-service'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { ALL_FIELDS, debounce, emptyForm, formFromMoment, legacyTime, toPayload, validate } from '@/utils/birth-moment'
import { COPY, MONTHS, arcReadout, closerAction, daysInMonth, isoFromParts, partsFromIso } from '@/utils/birth-ask'
import '@/assets/fonts/letter/letter-fonts.css'

const props = defineProps({
  // 'inline' (a card in the chat), 'modal' (a sheet on a phone, a modal on a
  // desktop), 'plain' (no chrome: inside BirthAsk's own card or sheet).
  mode: { type: String, default: 'inline' },
  title: { type: String, default: '' },
  // The one "why" line.
  subtitle: { type: String, default: '' },
  // A moment as GET /birth/moment/ returns it, or null.
  initial: { type: Object, default: null },
  fields: { type: Array, default: () => ALL_FIELDS },
  // Fields that may be left empty, each saved alone. Optional time and place
  // fold behind "I know my birth time too" on the ask.
  optional: { type: Array, default: () => [] },
  // The ask's button ("Show my star sign" when empty). The closer look's is
  // worked out from what is filled in unless one is given.
  submitLabel: { type: String, default: '' },
  saving: { type: Boolean, default: false },
  // Server field errors ({birthdate?, time?, place?, _?}) shown under their fields.
  serverErrors: { type: Object, default: null },
  // The sender's or requester's name, for the explainer's "You & <name>" row.
  withName: { type: String, default: '' },
  // The fold starts closed (it opens anyway when a time or place is saved).
  folded: { type: Boolean, default: true },
  // The "What's this?" pill.
  explain: { type: Boolean, default: true },
  // × in the head, and the "Not now" button (null: modal only).
  closable: { type: Boolean, default: null },
  dismissLabel: { type: String, default: null },
})
const emit = defineEmits(['save', 'close'])

const uid = `bm${Math.random().toString(36).slice(2, 8)}`
const reduced = useReducedMotion()
const shell = computed(() => (props.mode === 'modal' ? BirthSheet : 'div'))
const shellProps = computed(() => (props.mode === 'modal' ? { label: props.title || COPY.dateLabel } : { class: 'bm-wrap' }))
const isClosable = computed(() => (props.closable == null ? props.mode === 'modal' : props.closable))
const dismiss = computed(() => (props.dismissLabel == null ? (props.mode === 'modal' ? COPY.notNow : '') : props.dismissLabel))

const form = reactive(emptyForm())
const parts = reactive({ month: '', day: '', year: '' })
const touched = reactive({ date: false, submit: false })
const placeQuery = ref('')
const results = ref([])
const active = ref(-1)
const searching = ref(false)
const searched = ref('')
const showMap = ref(false)
const exactInput = ref(null)
const monthEl = ref(null)

const has = (f) => props.fields.includes(f)
const isOptional = (f) => props.optional.includes(f)
const isCloser = computed(() => !has('date') && (has('time') || has('place')))
const foldable = computed(() => has('date') && (has('time') || has('place'))
  && (!has('time') || isOptional('time')) && (!has('place') || isOptional('place')))

function seed (m) {
  Object.assign(form, formFromMoment(m))
  Object.assign(parts, partsFromIso(form.birthdate))
  placeQuery.value = (form.place && form.place.name) || ''
}
seed(props.initial)
const savedMore = (m) => !!(m && (m.time || m.place))
const foldOpen = ref(!props.folded || savedMore(props.initial))
watch(() => props.initial, (m) => {
  if (m && !form.birthdate) { seed(m); if (savedMore(m)) foldOpen.value = true }
})
const showTimePlace = computed(() => isCloser.value || !foldable.value || foldOpen.value)

const dayCount = computed(() => daysInMonth(parts.month, parts.year))
watch(() => [parts.month, parts.day, parts.year], () => {
  if (parts.day && Number(parts.day) > dayCount.value) parts.day = ''
  form.birthdate = isoFromParts(parts)
})
function onYear () { parts.year = String(parts.year || '').replace(/\D/g, '').slice(0, 4) }

// A field folded away is not asked (and not checked).
const askedFields = computed(() => props.fields.filter((f) => f === 'date' || showTimePlace.value))
const localErrors = computed(() => validate(form, { fields: askedFields.value, optional: props.optional }))
// Errors appear once the seeker has tried to save (or left a whole date), then
// the server's word wins for any field it named.
const shown = computed(() => {
  const out = {}
  const se = props.serverErrors || {}
  for (const k of ['birthdate', 'time', 'place']) {
    const local = localErrors.value[k]
    const visible = touched.submit || (k === 'birthdate' && touched.date && form.birthdate)
    out[k] = se[k] || (visible ? local : '')
  }
  return out
})

const readout = computed(() => arcReadout(form))
const closer = computed(() => closerAction(form))
const buttonLabel = computed(() => {
  if (props.submitLabel) return props.submitLabel
  return isCloser.value ? closer.value.label : COPY.saveDate
})

function pickWindow (key) { form.timeKind = 'window'; form.windowKey = key; form.exactTime = '' }
async function pickExact () {
  if (form.timeKind === 'exact') { form.timeKind = ''; return }
  form.timeKind = 'exact'; form.windowKey = ''
  await nextTick()
  if (exactInput.value) exactInput.value.focus()
}
function pickUnknown () {
  form.timeKind = form.timeKind === 'unknown' ? '' : 'unknown'
  form.windowKey = ''
}

const listOpen = computed(() => {
  const q = placeQuery.value.trim()
  if (q.length < 2 || searching.value || searched.value !== placeQuery.value) return false
  if (form.place && form.place.name === placeQuery.value) return false
  return true
})
const runSearch = debounce(async (q) => {
  if (q.trim().length < 2) { results.value = []; searching.value = false; return }
  try {
    const found = await searchBirthPlaces(q)
    if (placeQuery.value === q) { results.value = found || []; active.value = results.value.length ? 0 : -1; searched.value = q }
  } catch (e) {
    results.value = []; searched.value = q
  } finally { if (placeQuery.value === q) searching.value = false }
}, 250)
function onPlaceInput () {
  if (form.place && placeQuery.value !== form.place.name) form.place = null
  searching.value = placeQuery.value.trim().length >= 2
  runSearch(placeQuery.value)
}
function move (step) {
  if (!results.value.length) return
  active.value = (active.value + step + results.value.length) % results.value.length
}
function chooseActive () { if (active.value >= 0 && results.value[active.value]) choosePlace(results.value[active.value]) }
function choosePlace (r) {
  form.place = { latitude: r.lat, longitude: r.lng, name: r.name }
  placeQuery.value = r.name
  results.value = []
  active.value = -1
  showMap.value = false
}
function toggleMap () {
  showMap.value = !showMap.value
  if (showMap.value) { searched.value = ''; nextTick(() => setTimeout(() => window.dispatchEvent(new Event('resize')), 60)) }
}
function onMapPick ({ latitude, longitude }) {
  // The server names a map point when it saves it.
  form.place = { latitude, longitude, name: '' }
  placeQuery.value = ''
}

// "What's this?"
const explainOpen = ref(false)
const explained = ref(false)
function openExplainer () { explainOpen.value = true; explained.value = true }
function startFromExplainer () {
  explainOpen.value = false
  nextTick(() => { if (monthEl.value && monthEl.value.focus) monthEl.value.focus() })
}

function submit () {
  touched.submit = true
  if (Object.keys(localErrors.value).length) return
  if (isCloser.value && closer.value.disabled) return
  // Folded away means not given, even if something was picked before folding.
  const f = showTimePlace.value ? form : { ...form, timeKind: '', windowKey: '', exactTime: '', place: null }
  const payload = toPayload(f)
  emit('save', {
    ...payload,
    form: { ...f },
    // The old shape, for surfaces that still take one (Studio's reader birth).
    legacy: {
      birthdate: f.birthdate,
      birthtime: legacyTime(f),
      birthplace: f.place ? { latitude: f.place.latitude, longitude: f.place.longitude } : null,
    },
  })
}

onMounted(() => {
  // OpenLayers sizes itself off its container; nudge it once laid out.
  if (props.mode === 'modal') nextTick(() => setTimeout(() => window.dispatchEvent(new Event('resize')), 60))
})

defineExpose({ form, submit })
</script>

<style scoped>
.bm { --bm-bg: #14071f; --bm-ink: #e9dcff; --bm-dim: #9b86c9; --bm-gold: #f0d9a3; --bm-line: rgba(240, 217, 163, 0.22);
  --bm-display: 'Letter Display', 'Cinzel', Georgia, serif; --bm-serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  color: var(--bm-ink); font-family: var(--bm-serif); box-sizing: border-box; width: 100%; }
.bm--inline { background: rgba(20, 7, 31, 0.94); border: 1px solid var(--bm-line); border-radius: 16px; padding: 16px; }
.bm-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.bm-title { margin: 2px 0 0; font: 600 19px/1.25 var(--bm-display); letter-spacing: 0.03em; color: #f5e8c0; }
.bm-headr { display: flex; align-items: center; gap: 4px; margin-left: auto; }
.bm-q { display: inline-flex; align-items: center; gap: 7px; min-height: 36px; padding: 4px 12px 4px 5px; border-radius: 999px; cursor: pointer;
  background: rgba(240, 217, 163, 0.08); border: 1px solid rgba(240, 217, 163, 0.35); color: var(--bm-gold); font: 600 15px var(--bm-serif); white-space: nowrap; }
.bm-q b { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%;
  background: var(--bm-gold); color: #2a1845; font: 700 15px system-ui, -apple-system, 'Segoe UI', sans-serif; }
.bm-q:hover { background: rgba(240, 217, 163, 0.16); }
.bm-q.pulse b { animation: qPulse 2.4s ease-in-out 2; }
@keyframes qPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(240, 217, 163, 0.55) } 50% { box-shadow: 0 0 0 7px rgba(240, 217, 163, 0) } }
.bm-close { background: none; border: 0; color: var(--bm-dim); font-size: 26px; line-height: 1; cursor: pointer; min-width: 44px; min-height: 44px; margin: -8px -10px 0 0; }
.bm-why { margin: 8px 0 2px; font-size: 18px; line-height: 1.4; font-weight: 500; color: #dccdf5; }
.bm-why em { color: var(--bm-gold); font-style: normal; }
.bm-field { margin-top: 16px; position: relative; }
.bm-label { display: block; font: 600 11px var(--bm-display); letter-spacing: 0.14em; text-transform: uppercase; color: var(--bm-dim); margin-bottom: 8px; }
.bm-label i { font: italic 500 14px var(--bm-serif); letter-spacing: 0; text-transform: none; color: #8f7cb3; }
.bm-input, .bm-select { width: 100%; box-sizing: border-box; min-height: 48px; padding: 10px 12px; font: 600 18px var(--bm-serif); color: var(--bm-ink);
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(155, 134, 201, 0.45); border-radius: 12px; color-scheme: dark;
  appearance: none; -webkit-appearance: none; }
.bm-select { padding-right: 28px; cursor: pointer;
  background-image: linear-gradient(45deg, transparent 50%, var(--bm-dim) 50%), linear-gradient(135deg, var(--bm-dim) 50%, transparent 50%);
  background-position: calc(100% - 16px) 21px, calc(100% - 11px) 21px; background-size: 5px 5px; background-repeat: no-repeat; }
.bm-select option { background: #1d0c2c; color: var(--bm-ink); }
.bm-input::placeholder { color: #7d6b9c; font-weight: 500; }
.bm-input:focus, .bm-select:focus, .bm-q:focus-visible, .bm-save:focus-visible, .bm-more:focus-visible, .bm-ghost:focus-visible,
.bm-pin:focus-visible, .arc-links button:focus-visible { outline: 2px solid var(--bm-gold); outline-offset: 2px; }
.bm-date3 { display: grid; grid-template-columns: 1.6fr 1fr 1.2fr; gap: 8px; }
.bm-error { font-size: 15px; font-weight: 500; color: #f0a3a3; margin: 8px 0 0; }
.bm-more { display: flex; align-items: center; gap: 10px; width: 100%; margin-top: 16px; padding: 12px 14px; min-height: 48px; border-radius: 12px;
  cursor: pointer; text-align: left; background: rgba(255, 255, 255, 0.03); border: 1px dashed rgba(240, 217, 163, 0.35); color: #e6d7ff;
  font: 500 17px var(--bm-serif); }
.bm-more .plus { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--bm-gold); display: inline-flex; align-items: center;
  justify-content: center; color: var(--bm-gold); font: 600 16px system-ui, sans-serif; transition: transform 0.25s; }
.bm-more[aria-expanded="true"] .plus { transform: rotate(45deg); }
.bm-more small { display: block; font: 500 14px var(--bm-serif); color: var(--bm-dim); }
.bm-closer-in { animation: openDown 0.35s ease both; }
@keyframes openDown { from { opacity: 0; transform: translateY(-6px) } to { opacity: 1; transform: none } }
.arc-read { margin: 6px 0 0; text-align: center; min-height: 26px; font-size: 19px; font-weight: 600; color: var(--bm-gold); }
.arc-read span { font-weight: 500; color: var(--bm-dim); }
.arc-read.idle { color: var(--bm-dim); font-weight: 500; font-size: 17px; }
.arc-links { display: flex; justify-content: center; gap: 6px; flex-wrap: wrap; margin-top: 4px; }
.arc-links button { background: none; border: 0; cursor: pointer; color: #cdbbe8; text-decoration: underline; text-underline-offset: 3px;
  font: 500 16px var(--bm-serif); padding: 8px 6px; min-height: 44px; }
.arc-links button.on { color: var(--bm-gold); }
.arc-links .dot { color: #5d4c7a; align-self: center; }
.bm-exact { display: flex; justify-content: center; margin-top: 6px; }
.bm-exact .bm-input { max-width: 180px; text-align: center; }
.bm-place { position: relative; }
.bm-place .bm-input { padding-right: 52px; }
.bm-input.picked { border-color: rgba(240, 217, 163, 0.7); color: var(--bm-gold); }
.bm-pin { position: absolute; right: 4px; top: 4px; width: 40px; height: 40px; border-radius: 10px; border: 0; background: none; color: var(--bm-dim);
  cursor: pointer; display: flex; align-items: center; justify-content: center; }
.bm-pin:hover, .bm-pin.on { color: var(--bm-gold); background: rgba(240, 217, 163, 0.08); }
.bm-results { list-style: none; margin: 4px 0 0; padding: 4px; position: absolute; left: 0; right: 0; z-index: 5; background: #1d0c2c;
  border: 1px solid var(--bm-line); border-radius: 12px; max-height: 280px; overflow-y: auto; box-shadow: 0 14px 30px rgba(0, 0, 0, 0.5); }
.bm-result { width: 100%; text-align: left; min-height: 44px; padding: 8px 10px; background: none; border: 0; border-radius: 8px; color: var(--bm-ink);
  font: 500 17px var(--bm-serif); cursor: pointer; }
.bm-result.active, .bm-result:hover { background: rgba(240, 217, 163, 0.12); }
.bm-result.map { color: var(--bm-gold); border-top: 1px solid rgba(195, 169, 255, 0.12); border-radius: 0 0 8px 8px; }
.bm-searching { font-size: 15px; color: var(--bm-dim); margin: 6px 0 0; }
.bm-chosen { margin: 8px 0 0; font-size: 17px; font-weight: 600; color: var(--bm-gold); }
.bm-map { position: relative; margin-top: 8px; height: 240px; border-radius: 12px; overflow: hidden; border: 1px solid var(--bm-line);
  animation: openDown 0.3s ease both; }
.bm-map :deep(.birthplace-map-widget) { height: 240px; }
.bm-map-help { position: absolute; left: 10px; top: 8px; z-index: 2; margin: 0; font-size: 14px; color: #cdbbe8; pointer-events: none;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8); }
.bm-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px; flex-wrap: wrap; }
.bm-save { background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff; border: 1px solid rgba(240, 217, 163, 0.35); border-radius: 999px;
  padding: 11px 24px; min-height: 48px; font: 700 18px var(--bm-serif); cursor: pointer; box-shadow: 0 8px 24px -10px rgba(123, 75, 176, 0.9); }
.bm-save:disabled { opacity: 0.5; cursor: default; box-shadow: none; }
.bm-ghost { background: transparent; color: var(--bm-dim); border: 1px solid rgba(155, 134, 201, 0.4); border-radius: 999px; padding: 10px 18px;
  min-height: 48px; font: 600 17px var(--bm-serif); cursor: pointer; }
.bm-fine { margin: 10px 0 0; font: italic 500 15px var(--bm-serif); color: #8f7cb3; text-align: right; }
.bm-privacy { margin-top: 4px; }
.bm-hint { margin: 10px 0 0; font: 500 15px var(--bm-serif); color: var(--bm-dim); text-align: right; }
@media (max-width: 600px) {
  .bm--inline { padding: 14px; }
  .bm-actions .bm-save { flex: 1; }
  .bm-fine, .bm-hint { text-align: center; }
  .bm-title { font-size: 18px; }
}
@media (prefers-reduced-motion: reduce) {
  .bm-q.pulse b, .bm-closer-in, .bm-map { animation: none; }
  .bm-more .plus { transition: none; }
}
</style>
